import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TNil } from '@app/app.interface';
import { ICtaForm, ICustomCtaForm, ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { ICustomValueConfig } from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EOverwriteStatus } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { snakeCase, uniqBy } from 'lodash';
import { ILastActiveElementDetails, TRemoveConfirmationType } from './cta-details.interface';
import { IExecution } from './deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';

@Injectable({
  providedIn: 'root',
})
export class CtaDetailsService {
  private readonly customValueCache = new Map<string, boolean>();

  private readonly customValuesService = inject(CustomValuesService);

  public static isLastCustomCtaElement(
    form: FormArray<FormGroup>,
    isCustomMode: boolean,
    creatorId: string | TNil,
  ): boolean {
    if (!creatorId) {
      return false;
    }

    const formArray = form as FormArray<FormGroup<ICustomCtaForm>>;

    return (
      formArray.controls.filter(
        (control) =>
          (!isCustomMode && !control.value.exclusiveTo) ||
          (isCustomMode &&
            (control.value.exclusiveTo === creatorId ||
              !control.value.customCtaValues?.some(
                (customCtaValue) =>
                  customCtaValue.creatorId === creatorId &&
                  customCtaValue.valueName === null &&
                  customCtaValue.status === EOverwriteStatus.deleted,
              ))),
      )?.length === 1
    );
  }

  public static getLastActiveElementCreatorNames(
    form: FormArray<FormGroup>,
    isCustomMode: boolean,
    creators: ICreator[],
  ): Map<string, ILastActiveElementDetails> {
    const formArray = form as FormArray<FormGroup<ICustomCtaForm & { id: FormControl<string> }>>;
    const lastElementDetailsMap: Map<string, ILastActiveElementDetails> = new Map();
    const sortedCreators = creators.sort((a, b) => a.name.localeCompare(b.name));

    for (const creator of sortedCreators) {
      const filteredControls = formArray.controls.filter(
        (control) =>
          !isCustomMode &&
          !control.value.customCtaValues?.some(
            (customCtaValue) =>
              customCtaValue.creatorId === creator.id &&
              customCtaValue.valueName === null &&
              customCtaValue.status === EOverwriteStatus.deleted,
          ),
      );

      if (filteredControls?.length === 1) {
        if (lastElementDetailsMap.has(filteredControls[0].value.id ?? '')) {
          lastElementDetailsMap.get(filteredControls[0].value.id ?? '')!.creatorNames += `, ${creator.name}`;
        } else {
          lastElementDetailsMap.set(filteredControls[0].value.id ?? '', {
            creatorNames: creator.name,
            elementId: filteredControls[0].value.id ?? '',
          });
        }
      }
    }

    return lastElementDetailsMap;
  }

  public static openRemoveConfirmationDialog(
    dialog: MatDialog,
    translate: TranslateService,
    customValueConfig: ICustomValueConfig | FormGroup,
    creators: ICreator[],
    type: TRemoveConfirmationType,
  ): MatDialogRef<ConfirmDialogComponent, unknown> | undefined {
    const creatorIds: string[] = [];
    const parentForm = ((customValueConfig as ICustomValueConfig).componentFields?.getForm() ??
      customValueConfig) as FormGroup<ICustomCtaForm>;
    const childFormArray = (customValueConfig as ICustomValueConfig).nestedArray?.getFormArray() as FormArray<
      FormGroup<ICustomCtaForm>
    >;

    parentForm.get('customCtaValues')?.value?.forEach((customCtaValue) => {
      if (customCtaValue.valueName === null || customCtaValue.status === EOverwriteStatus.deleted) {
        return;
      }

      creatorIds.push(customCtaValue.creatorId);
    });

    childFormArray?.controls?.forEach((childForm) => {
      childForm.get('customCtaValues')?.value?.forEach((customCtaValue) => {
        if (customCtaValue.valueName === null || customCtaValue.status === EOverwriteStatus.deleted) {
          return;
        }

        creatorIds.push(customCtaValue.creatorId);
      });
    });

    if (creatorIds.length) {
      const typeTranslation = String(
        translate.instant(`pages.campaignForm.ctaDialog.removeConfirmation.types.${type}`),
      );

      const dialogRef = dialog.open(ConfirmDialogComponent, {
        data: {
          cancelText: translate.instant('shared.cancel'),
          confirmText: translate.instant('shared.remove'),
          iconList: uniqBy(
            creatorIds
              .map((creatorId) => {
                const creator = creators.find((c) => c.id === creatorId);

                return {
                  icon: creator?.icon ?? '',
                  id: creatorId,
                  name: creator?.name ?? '',
                };
              })
              .sort((a, b) => a.name.localeCompare(b.name)),
            'id',
          ),
          message: translate.instant('pages.campaignForm.ctaDialog.ctaDetails.followingCreatorsHaveCustomValues', {
            type: typeTranslation.toLowerCase(),
          }),
          swapButtons: false,
          title: translate.instant(`pages.campaignForm.ctaDialog.removeConfirmation.title`, {
            type: typeTranslation,
          }),
        } as IConfirmDialogData,
      });

      return dialogRef;
    }

    return;
  }

  public clearCustomValueCache(): void {
    this.customValueCache.clear();
  }

  public getCustomValueState(fieldName: string, creatorId: string, customValueConfig: ICustomValueConfig): boolean {
    if (!this.customValueCache.has(fieldName)) {
      this.customValueCache.set(fieldName, this.hasCustomValue(fieldName, creatorId, customValueConfig));
    }

    return this.customValueCache.get(fieldName)!;
  }

  public onDeleteCheckboxChange(
    fieldName: string,
    event: MatCheckboxChange,
    form: FormGroup<ICtaForm>,
    creatorId: string,
    customValueConfig: ICustomValueConfig,
  ): void {
    if (event?.checked && fieldName === 'id') {
      this.enableForm(form);
    }

    this.mainFormCheckboxChange(fieldName, event, form, creatorId, customValueConfig);
  }

  public getCtaDisplayNameForSqlData(
    ctaName: string | TNil,
    executions: Partial<Pick<IExecution, 'name' | 'exclusiveTo'>>[],
  ): string {
    const creatorExecutions = Array.from(executions.values()).filter((execution) => !execution.exclusiveTo) ?? [];

    return creatorExecutions?.length > 1 ? (ctaName ?? '') : (creatorExecutions?.[0]?.name ?? '');
  }

  private updateCustomValueCache(fieldName: string, creatorId: string, customValueConfig: ICustomValueConfig): void {
    this.customValueCache.set(fieldName, this.hasCustomValue(fieldName, creatorId, customValueConfig));
  }

  private mainFormCheckboxChange(
    fieldName: string,
    event: MatCheckboxChange,
    form: FormGroup<ICtaForm>,
    creatorId: string,
    customValueConfig: ICustomValueConfig,
  ): void {
    this.customValuesService.customDelete(customValueConfig, creatorId, fieldName);

    if (!event?.checked) {
      this.disableForm(form);
    }

    this.updateCustomValueCache(fieldName, creatorId, customValueConfig);
  }

  private enableForm(form: FormGroup<ICtaForm>): void {
    form.get('name')?.enable({ emitEvent: false });
  }

  private disableForm(form: FormGroup<ICtaForm>): void {
    form.get('name')?.disable({ emitEvent: false });
  }

  private hasCustomValue(fieldName: string, creatorId: string, customValueConfig: ICustomValueConfig): boolean {
    return (
      (customValueConfig.componentFields?.getForm()?.get('customCtaValues')?.value as ICustomCtaValue[]) ?? []
    ).some(
      (value) =>
        value.creatorId === creatorId &&
        (value.valueName === snakeCase(fieldName) || (value.valueName === null && fieldName === 'id')),
    );
  }
}
